import { useState } from "react";
import {
  Alert,
  Box,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import {
  getNotificationPermission,
  loadNotifyPreference,
  NotificationPermissionState,
  requestNotificationPermission,
  saveNotifyPreference,
} from "../chatNotifications";

/**
 * The user's way back in after any answer they gave the soft-ask — including
 * "not now", which the soft-ask stops offering after a couple of declines.
 *
 * Browser permission and our preference are separate switches on purpose. The
 * permission is irreversible-ish (a "Block" can only be undone in browser site
 * settings, which we cannot open for them), so turning the feature off here
 * leaves the permission intact and the decision reversible.
 */
export function NotificationsTab() {
  const [permission, setPermission] = useState<NotificationPermissionState>(() =>
    getNotificationPermission()
  );
  const [enabled, setEnabled] = useState(() => loadNotifyPreference());

  const onToggle = async (next: boolean) => {
    setEnabled(next);
    saveNotifyPreference(next);
    // Switching on is a click, which is the user gesture the browsers require —
    // so this is a valid moment to ask if we never have.
    if (next && permission === "default") {
      setPermission(await requestNotificationPermission());
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <FormControlLabel
        control={
          <Switch
            checked={enabled}
            onChange={e => onToggle(e.target.checked)}
            disabled={permission === "unsupported"}
            data-cy="chat-notifications-toggle"
          />
        }
        label="Notify me when a chat finishes"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        Only when this tab is not focused. Notifications say a response is
        ready — never what it says.
      </Typography>

      {permission === "denied" && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Your browser is blocking notifications for this site. We cannot ask
          again from here — turn them back on in your browser&apos;s site
          settings for this page (the icon at the left of the address bar).
        </Alert>
      )}

      {permission === "unsupported" && (
        <Alert severity="info" sx={{ mt: 2 }}>
          This browser does not support notifications from a page. On Android,
          notifications require an installed app rather than a browser tab.
        </Alert>
      )}
    </Box>
  );
}
